import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  ThumbDownOutlined as ThumbDownOutlinedIcon,
  AccessTime as TimeIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { format } from 'date-fns';
import { useHelpArticle, useRelatedArticles } from '../../hooks/useHelp';
import { useHelpMutations } from '../../hooks/useHelpMutations';

interface ArticleViewerProps {
  slug: string;
  onSelectRelated: (slug: string) => void;
}

export const ArticleViewer: React.FC<ArticleViewerProps> = ({
  slug,
  onSelectRelated,
}) => {
  const { data: article, isLoading, error } = useHelpArticle(slug);
  const { data: relatedArticles = [] } = useRelatedArticles(slug, {
    enabled: !!article,
  });
  const { submitFeedback } = useHelpMutations();

  const [feedbackGiven, setFeedbackGiven] = React.useState<'helpful' | 'not_helpful' | null>(null);

  // Reset feedback when article changes
  useEffect(() => {
    setFeedbackGiven(null);
  }, [slug]);

  const handleFeedback = async (helpful: boolean) => {
    if (!article || feedbackGiven) return;

    try {
      await submitFeedback.mutateAsync({ articleId: article._id, helpful });
      setFeedbackGiven(helpful ? 'helpful' : 'not_helpful');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error || !article) {
    return (
      <Box textAlign="center" py={4}>
        <Typography color="error">
          Failed to load article
        </Typography>
      </Box>
    );
  }

  // Parse markdown content and sanitize to prevent XSS
  const htmlContent = DOMPurify.sanitize(marked.parse(article.content) as string);

  return (
    <Box>
      {/* Article Header */}
      <Box mb={3}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {article.title}
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <TimeIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              Updated {format(new Date(article.lastUpdated), 'MMM d, yyyy')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <ViewIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {article.viewCount} views
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Chip label={article.category} size="small" />
          <Chip label={article.type} size="small" variant="outlined" />
        </Stack>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Article Content */}
      <Box
        sx={{
          '& h1, & h2, & h3': {
            mt: 3,
            mb: 1.5,
            fontWeight: 600,
          },
          '& h1': { fontSize: '1.5rem' },
          '& h2': { fontSize: '1.25rem' },
          '& h3': { fontSize: '1.1rem' },
          '& p': {
            mb: 2,
            lineHeight: 1.7,
          },
          '& ul, & ol': {
            pl: 3,
            mb: 2,
          },
          '& li': {
            mb: 0.5,
          },
          '& code': {
            bgcolor: 'grey.100',
            px: 0.75,
            py: 0.25,
            borderRadius: 0.5,
            fontSize: '0.875em',
            fontFamily: 'monospace',
          },
          '& pre': {
            bgcolor: 'grey.900',
            color: 'grey.100',
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
            '& code': {
              bgcolor: 'transparent',
              p: 0,
              color: 'inherit',
            },
          },
          '& blockquote': {
            borderLeft: 4,
            borderColor: 'primary.main',
            pl: 2,
            ml: 0,
            fontStyle: 'italic',
            color: 'text.secondary',
          },
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
          '& img': {
            maxWidth: '100%',
            borderRadius: 1,
          },
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            mb: 2,
          },
          '& th, & td': {
            border: 1,
            borderColor: 'divider',
            p: 1,
            textAlign: 'left',
          },
          '& th': {
            bgcolor: 'grey.50',
            fontWeight: 600,
          },
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />

      <Divider sx={{ my: 3 }} />

      {/* Feedback */}
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="subtitle2" gutterBottom>
          Was this article helpful?
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center">
          <Tooltip title="Yes, helpful">
            <IconButton
              onClick={() => handleFeedback(true)}
              disabled={!!feedbackGiven}
              color={feedbackGiven === 'helpful' ? 'primary' : 'default'}
            >
              {feedbackGiven === 'helpful' ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="No, not helpful">
            <IconButton
              onClick={() => handleFeedback(false)}
              disabled={!!feedbackGiven}
              color={feedbackGiven === 'not_helpful' ? 'error' : 'default'}
            >
              {feedbackGiven === 'not_helpful' ? <ThumbDownIcon /> : <ThumbDownOutlinedIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
        {feedbackGiven && (
          <Typography variant="caption" color="text.secondary">
            Thanks for your feedback!
          </Typography>
        )}
      </Paper>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>
            Related Articles
          </Typography>
          <Stack spacing={1}>
            {relatedArticles.map((related) => (
              <Paper
                key={related.id}
                onClick={() => onSelectRelated(related.slug)}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {related.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {related.category}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default ArticleViewer;
